import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, interval, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { InFlightInterceptor } from '../http-interceptors/in-flight-interceptor';

/**
 * Aggregates "is it safe to silently reload the app right now?" across
 * dirty forms, in-flight mutations, and open dialogs.
 *
 * Used by AppUpdateService to gate silent reloads on non-critical
 * updates — we never want to nuke a user mid-entry.
 */

export interface ReadinessState {
  safe: boolean;
  reason?: string;
}

@Injectable({ providedIn: 'root' })
export class ReloadReadinessService {
  constructor(
    private inFlight: InFlightInterceptor,
    private dialog: MatDialog,
  ) {}

  readonly state$: Observable<ReadinessState> = combineLatest([
    this.inFlight.pendingMutations$.pipe(startWith(0)),
    interval(500).pipe(startWith(0)),
  ]).pipe(
    map(([pending]) => this.evaluate(pending)),
    distinctUntilChanged((a, b) => a.safe === b.safe && a.reason === b.reason),
  );

  snapshot(): ReadinessState {
    return this.evaluate(this.inFlight.currentPendingMutations);
  }

  private evaluate(pendingMutations: number): ReadinessState {
    if (pendingMutations > 0) {
      return { safe: false, reason: 'in_flight_mutation' };
    }
    if (this.dialog.openDialogs.length > 0) {
      return { safe: false, reason: 'dialog_open' };
    }
    if (this.hasDirtyForm()) {
      return { safe: false, reason: 'dirty_form' };
    }
    return { safe: true };
  }

  private hasDirtyForm(): boolean {
    // Angular adds .ng-dirty to any form control the user has edited.
    // Querying the DOM avoids needing every feature to register its
    // FormGroup with this service.
    return document.querySelector('form.ng-dirty, .ng-dirty > form, form .ng-dirty') !== null;
  }
}
